// DISCLAIMER
// Copyright 2025-2026 ArangoDB GmbH, Cologne, Germany

import React, { useEffect, useState } from "react";
import { Table, Button, Icon, Label, Message } from "semantic-ui-react";
import { Confirm, ConfirmInfo, ErrorMessage } from "../../ui/lib";
import { DefaultChart, DeploymentChartLayers, DeploymentChartOverrides } from "../../api/charts/v1/icharts";
import apiClients from "../../api/apiclients";
import { useDeploymentStore } from "../../util/storage/DeploymentStore";
import { withRefresh, IWithRefreshProps } from "../../util/WithRefresh";
import { hasSupportPermission, Permission } from "../../util/PermissionCache";
import { decodeValuesObject, downloadText, errorText, flattenValues, SourceLabel } from "../charts/utils";
import ValuesModal from "../charts/ValuesModal";
import ChartOverridesModal from "./ChartOverridesModal";

const {
  ListDefaultCharts,
  AddDeploymentChart,
  RemoveDeploymentChart,
  SetDeploymentChartOverrides,
  ResetDeploymentChart,
  SyncDeploymentCharts,
  ListDeploymentChartLayers,
  ExportDeploymentCharts,
} = apiClients.idashboardClient;

const stripArangoPrefix = (name: string): { prefix: string; rest: string } => {
  if (name.startsWith("arangodb-")) return { prefix: "arangodb-", rest: name.slice(9) };
  if (name.startsWith("arango-")) return { prefix: "arango-", rest: name.slice(7) };
  return { prefix: "", rest: name };
};

const ChartName = ({ name }: { name?: string }) => {
  const { prefix, rest } = stripArangoPrefix(name || "");
  return (
    <>
      {prefix && <span style={{ color: "#aaa" }}>{prefix}</span>}
      {rest}
    </>
  );
};

const byName = (a: { name?: string }, b: { name?: string }) => stripArangoPrefix(a.name || "").rest.localeCompare(stripArangoPrefix(b.name || "").rest);

const HelmChartsTab = (props: IWithRefreshProps) => {
  const { deployment } = useDeploymentStore();
  const hasPermission = (permission: Permission) => hasSupportPermission(permission, props.hasPermissionByUrl);
  const canList = hasPermission("internal-dashboard.deploymentchart.get");
  const canCreate = hasPermission("internal-dashboard.deploymentchart.create");
  const canUpdate = hasPermission("internal-dashboard.deploymentchart.update");
  const canDelete = hasPermission("internal-dashboard.deploymentchart.delete");
  const canListDefaults = hasPermission("internal-dashboard.defaultchart.list");

  const [layers, setLayers] = useState<DeploymentChartLayers[]>([]);
  const [defaults, setDefaults] = useState<DefaultChart[]>([]);
  const [editing, setEditing] = useState<{ layers?: DeploymentChartLayers }>();
  const [valuesFor, setValuesFor] = useState<DeploymentChartLayers>();
  const [confirmInfo, setConfirmInfo] = useState<ConfirmInfo | undefined>();
  const [error, setError] = useState<any>();

  const refresh = async () => {
    try {
      const [layerList, defaultList] = await Promise.all([
        canList ? ListDeploymentChartLayers({ deployment_id: deployment.id }) : Promise.resolve(undefined),
        canListDefaults ? ListDefaultCharts() : Promise.resolve(undefined),
      ]);
      setLayers(((layerList && layerList.items) || []).slice().sort(byName));
      setDefaults(((defaultList && defaultList.items) || []).slice().sort(byName));
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    refresh();
  }, [deployment.id]);

  // run performs an API call and shows the charts again.
  const run = async (action: () => Promise<any>) => {
    try {
      await action();
      setEditing(undefined);
    } catch (err) {
      setError(err);
    }
    await refresh();
  };

  const confirm = (header: string, content: string, action: () => Promise<any>) =>
    setConfirmInfo({
      header,
      content,
      onConfirm: async () => {
        setConfirmInfo(undefined);
        await run(action);
      },
      onDenied: () => setConfirmInfo(undefined),
    });

  // A chart that is not one of the default charts is added with its overrides, a default chart is changed.
  const handleSave = (name: string, overrides: DeploymentChartOverrides) =>
    run(() =>
      editing && editing.layers
        ? SetDeploymentChartOverrides({ deployment_id: deployment.id, name, overrides })
        : AddDeploymentChart({ deployment_id: deployment.id, name, version: overrides.version, enabled: overrides.enabled, values: overrides.values })
    );

  // A default chart is added without overrides, it follows the default chart.
  const handleAddDefault = (chart: DefaultChart) =>
    run(async () => {
      await AddDeploymentChart({ deployment_id: deployment.id, name: chart.name, version: chart.version, enabled: chart.enabled });
      await SetDeploymentChartOverrides({ deployment_id: deployment.id, name: chart.name, overrides: {} });
    });

  const handleReset = (chart: DeploymentChartLayers) =>
    confirm(`Reset ${chart.name}?`, "The deployment overrides of the chart are removed, it follows the default chart again.", () =>
      ResetDeploymentChart({ deployment_id: deployment.id, name: chart.name })
    );

  const handleRemove = (chart: DeploymentChartLayers) =>
    confirm(`Remove ${chart.name}?`, "The chart is removed from the deployment. A default chart can be added again from the available charts.", () =>
      RemoveDeploymentChart({ deployment_id: deployment.id, name: chart.name })
    );

  const outOfDate = layers.filter((l) => l.out_of_date).length;
  const handleSync = () =>
    confirm(
      "Sync with the default charts?",
      "The charts are compiled again from the template, the configuration and their deployment overrides. The overrides are kept, and no charts are added.",
      () => SyncDeploymentCharts({ deployment_id: deployment.id })
    );

  const handleDownload = async () => {
    try {
      const response = await ExportDeploymentCharts({ deployment_id: deployment.id });
      downloadText(`deployment-${deployment.id}-charts.yaml`, response.yaml || "");
    } catch (err) {
      setError(err);
    }
  };

  const onDeployment = new Set(layers.map((l) => l.name));
  const available = defaults.filter((d) => !onDeployment.has(d.name));

  if (!canList) {
    return <Message warning content="You do not have permission to list the charts of this deployment." />;
  }

  return (
    <>
      <Confirm confirmInfo={confirmInfo} />
      {editing && (
        <ChartOverridesModal
          deploymentId={deployment.id || ""}
          layers={editing.layers}
          existingNames={layers.map((l) => l.name || "")}
          defaultNames={defaults.map((d) => d.name || "")}
          canEdit={editing.layers ? canUpdate : canCreate}
          onSave={handleSave}
          onClose={() => setEditing(undefined)}
        />
      )}
      {valuesFor && (
        <ValuesModal
          name={valuesFor.name}
          values={valuesFor.chart && valuesFor.chart.values}
          valueSources={valuesFor.value_sources}
          sources={["template", "configuration", "deployment"]}
          onClose={() => setValuesFor(undefined)}
        />
      )}
      <ErrorMessage active={!!error} onDismiss={() => setError(undefined)} message={errorText(error)} />

      <div style={{ marginBottom: "1rem" }}>
        {canUpdate && (
          <Button size="small" icon labelPosition="left" positive={outOfDate > 0} onClick={handleSync}>
            <Icon name="sync" />
            Sync{outOfDate > 0 ? ` (${outOfDate} out of date)` : ""}
          </Button>
        )}
        {canCreate && (
          <Button size="small" icon labelPosition="left" onClick={() => setEditing({})}>
            <Icon name="plus" />
            Add chart
          </Button>
        )}
        <Button size="small" icon labelPosition="left" onClick={handleDownload} title="The compiled charts, as the data cluster gets them">
          <Icon name="download" />
          Download compiled
        </Button>
      </div>

      <Table striped compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Chart</Table.HeaderCell>
            <Table.HeaderCell>Bundle</Table.HeaderCell>
            <Table.HeaderCell>Version</Table.HeaderCell>
            <Table.HeaderCell>Enabled</Table.HeaderCell>
            <Table.HeaderCell>Mode</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Values</Table.HeaderCell>
            <Table.HeaderCell collapsing />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {layers.map((item) => {
            const chart = item.chart || {};
            const valueCount = Object.keys(flattenValues(decodeValuesObject(chart.values))).length;
            const overridden = !!chart.override;
            return (
              <Table.Row key={item.name} warning={!!item.out_of_date}>
                <Table.Cell>
                  <ChartName name={item.name} />
                </Table.Cell>
                <Table.Cell>{chart.bundle}</Table.Cell>
                <Table.Cell>
                  {chart.version} <SourceLabel source={item.version_source} />
                </Table.Cell>
                <Table.Cell>
                  <Icon name={chart.enabled ? "check" : "minus"} /> <SourceLabel source={item.enabled_source} />
                </Table.Cell>
                <Table.Cell>
                  <Label
                    size="tiny"
                    basic
                    color={overridden ? "purple" : undefined}
                    title={overridden ? "Has deployment overrides" : "Follows the default chart"}
                  >
                    {overridden ? "overrides" : "default"}
                  </Label>
                  {item.out_of_date && (
                    <Label size="tiny" color="orange" title="The compiled chart differs from the layers, sync to compile it again">
                      out of date
                    </Label>
                  )}
                  {!item.in_defaults && (
                    <Label size="tiny" basic>
                      not in defaults
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>{(chart.status && chart.status.Status) || "Install prep"}</Table.Cell>
                <Table.Cell>
                  {valueCount === 0 ? (
                    "-"
                  ) : (
                    <Button basic compact size="mini" onClick={() => setValuesFor(item)}>
                      {valueCount} {valueCount === 1 ? "value" : "values"}
                    </Button>
                  )}
                </Table.Cell>
                <Table.Cell collapsing>
                  <Button
                    basic
                    compact
                    size="mini"
                    icon={canUpdate ? "pencil" : "eye"}
                    title={canUpdate ? "Edit the overrides" : "Show"}
                    onClick={() => setEditing({ layers: item })}
                  />
                  {canUpdate && overridden && item.in_defaults && (
                    <Button basic compact size="mini" icon="undo" title="Remove the overrides" onClick={() => handleReset(item)} />
                  )}
                  {canDelete && <Button basic compact size="mini" icon="trash" title="Remove" onClick={() => handleRemove(item)} />}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {available.length > 0 && (
        <>
          <h4>Available charts</h4>
          <p>Default charts that are not on this deployment.</p>
          <Table compact size="small">
            <Table.Body>
              {available.map((chart) => (
                <Table.Row key={chart.name}>
                  <Table.Cell>
                    <ChartName name={chart.name} />
                  </Table.Cell>
                  <Table.Cell>{chart.version}</Table.Cell>
                  <Table.Cell>
                    <Icon name={chart.enabled ? "check" : "minus"} />
                  </Table.Cell>
                  <Table.Cell collapsing>
                    {canCreate && canUpdate && <Button basic compact size="mini" icon="plus" content="Add" onClick={() => handleAddDefault(chart)} />}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </>
  );
};

export default withRefresh()(HelmChartsTab);
